import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { parseCsv } from '../../utils/csv';
import { IMPORT_TYPES, autoMap, positionalMap } from '../../utils/importTypes';
import { extractErrorMessage } from '../../utils/apiErrors';
import ImportPreviewTable from './ImportPreviewTable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DataImportDialog({ kind, open, onOpenChange, onImported }) {
  const cfg = IMPORT_TYPES[kind];
  const [text, setText] = useState('');
  const [step, setStep] = useState('input');
  const [hasHeader, setHasHeader] = useState(true);
  const [mapping, setMapping] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const grid = useMemo(() => (text.trim() ? parseCsv(text) : []), [text]);
  const width = grid.reduce((m, r) => Math.max(m, r.length), 0);
  const headers = hasHeader && grid.length ? grid[0] : Array.from({ length: width }, (_, i) => `Column ${i + 1}`);
  const rows = hasHeader ? grid.slice(1) : grid;

  const mappedRows = useMemo(() => rows.map((r) => {
    const o = {};
    mapping.forEach((f, i) => { if (f && r[i]) o[f] = r[i]; });
    return o;
  }), [rows, mapping]);

  const reset = () => { setText(''); setStep('input'); setMapping([]); setPreview(null); setDone(null); setHasHeader(true); };
  const close = (o) => { if (!o) reset(); onOpenChange(o); };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ''));
    reader.readAsText(f);
    e.target.value = '';
  };

  const goPreview = () => {
    if (!grid.length) return;
    const firstAuto = autoMap(kind, grid[0]);
    const detected = firstAuto.filter(Boolean).length >= 1 && hasHeader;
    setHasHeader(detected);
    setMapping(detected ? firstAuto : positionalMap(kind, width));
    setStep('preview');
  };

  useEffect(() => {
    if (step !== 'preview' || !mappedRows.length) { setPreview(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/import/${kind}?dry_run=true`, { rows: mappedRows });
        if (!cancelled) setPreview(res.data);
      } catch (err) {
        if (!cancelled) toast.error(extractErrorMessage(err, 'Preview failed'));
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [step, mappedRows, kind]);

  const importable = (preview?.added || 0) + (preview?.merged || 0);
  const missingRequired = cfg.fields.filter((f) => f.required && !mapping.includes(f.key));

  const commit = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/import/${kind}?dry_run=false`, { rows: mappedRows });
      setDone(res.data);
      setStep('done');
      toast.success(`Imported ${res.data.added + res.data.merged} ${cfg.label.toLowerCase()} row${res.data.added + res.data.merged === 1 ? '' : 's'}`);
      onImported?.();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Import failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-4xl" data-testid="data-import-dialog">
        <DialogHeader>
          <DialogTitle>Import {cfg.label} from CSV</DialogTitle>
          <DialogDescription>{cfg.hint}</DialogDescription>
        </DialogHeader>

        {step === 'input' && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50">
                <Upload size={14} /> Upload .csv
                <input type="file" accept=".csv,.txt,text/csv" className="hidden" onChange={onFile} data-testid="import-file-input" />
              </label>
              <Button type="button" variant="ghost" size="sm" onClick={() => setText(cfg.example)} data-testid="import-example-btn">
                <FileSpreadsheet size={14} className="mr-1.5" /> Load example
              </Button>
              <div className="flex items-center gap-2 ml-auto text-sm">
                <Switch checked={hasHeader} onCheckedChange={setHasHeader} data-testid="import-header-switch" />
                <span>First row is a header</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>CSV rows</Label>
              <Textarea rows={10} value={text} onChange={(e) => setText(e.target.value)} placeholder={cfg.example} className="font-mono text-xs" data-testid="import-textarea" />
            </div>
            <p className="text-xs text-slate-500">Columns: {cfg.fields.map((f) => f.label + (f.required ? ' *' : '')).join(' · ')}</p>
            <Button type="button" onClick={goPreview} disabled={!grid.length} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="import-next-btn">
              Preview {rows.length} row{rows.length === 1 ? '' : 's'}
            </Button>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <button type="button" onClick={() => setStep('input')} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900" data-testid="import-back-btn"><ArrowLeft size={14} /> Back</button>
              <div className="flex items-center gap-2">
                <Switch checked={hasHeader} onCheckedChange={(v) => { setHasHeader(v); setMapping(v ? autoMap(kind, grid[0] || []) : positionalMap(kind, width)); }} data-testid="import-header-switch-preview" />
                <span>First row is a header</span>
              </div>
              {preview && (
                <p className="ml-auto text-slate-700" data-testid="import-summary">
                  <span className="font-semibold text-emerald-700">{preview.added} new</span>
                  {preview.merged > 0 && <> · <span className="font-semibold text-sky-700">{preview.merged} merge</span></>}
                  {' · '}<span className="text-slate-500">{preview.skipped} skip</span>
                  {preview.invalid > 0 && <> · <span className="font-semibold text-red-700">{preview.invalid} invalid</span></>}
                </p>
              )}
            </div>
            {missingRequired.length > 0 && (
              <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="import-missing-required">
                Map a column to: {missingRequired.map((f) => f.label).join(', ')}
              </p>
            )}
            <ImportPreviewTable kind={kind} headers={headers} mapping={mapping} rows={rows} preview={preview}
              onMapChange={(i, v) => setMapping((m) => { const n = [...m]; n[i] = v; return n; })} />
            <Button type="button" onClick={commit} disabled={busy || !preview || !importable || missingRequired.length > 0} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="import-commit-btn">
              {busy ? 'Importing...' : `Import ${importable} row${importable === 1 ? '' : 's'}`}
            </Button>
          </div>
        )}

        {step === 'done' && done && (
          <div className="text-center py-8 space-y-3" data-testid="import-done">
            <CheckCircle2 size={40} className="mx-auto text-emerald-600" />
            <p className="text-lg font-semibold text-slate-900">Import complete</p>
            <p className="text-sm text-slate-600">{done.added} new · {done.merged} merged · {done.skipped} skipped · {done.invalid} invalid</p>
            <div className="flex justify-center gap-2 pt-2">
              <Button type="button" variant="outline" onClick={reset} data-testid="import-again-btn">Import more</Button>
              <Button type="button" onClick={() => close(false)} className="bg-slate-900" data-testid="import-close-btn">Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
